import Stepper from 'components/Stepper/Stepper';
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useStore } from 'store';
import logo from './img/logo.svg'
import { parseError } from 'utils/helpers';
import axios from 'axios'
import classNames from 'classnames';
import GoogleSign from 'components/GoogleSign/GoogleSign';
import Loader from 'components/Base/Loader';
import ConfirmationCode from 'components/ConfirmationCode/ConfirmationCode';
import BaseModal from 'components/Base/BaseModal';

const VerificationGates = () => {
    const navigate = useNavigate()
    const [step, setStep] = useState(1);

    const [timeLeft, setTimeLeft] = useState(0);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);

    const [modalState, setModalState] = useState("main");
    const [resetStep, setResetStep] = useState(0);
    const [resetCode, setResetCode] = useState('');

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordRepeat, setPasswordRepeat] = useState('');

    const [isGoogleSignUp, setIsGoogleSignUp] = useState(false);

    const [zipCode, setZipCode] = useState('');

    const [name, setName] = useState('');
    const [surname, setSurname] = useState('');

    const [termsAccept, setTermsAccept] = useState(false);
    const [termsError, setTermsError] = useState(false);

    const [isUserExist, setIsUserExist] = useState(false);


    const [otpCode, setOtpCode] = useState('');
    const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

    const [finishing, setFinishing] = useState(false);
    const [error, setError] = useState({})
    const [isLoading, setIsLoading] = useState(false);

    const store = useStore();

    useEffect(() => {
        if (timeLeft > 0) {
            const timerId = setTimeout(() => {
                setTimeLeft((prev) => prev - 1);
            }, 1000);
            return () => clearTimeout(timerId);
        }
    }, [timeLeft]);

    const verifyEmailCode = useCallback((e) => {
        e.preventDefault();

        if (!otpCode || otpCode.length < 4) {
            return setError({ message: 'Please enter a valid code' });
        }

        setIsVerifyingOtp(true);
        setError({});

        axios
            .post('/auth/signup/verify/enter', {
                email,
                code: otpCode.join(''),
            })
            .then(() => {
                setStep(5);
            })
            .catch((e) => {
                setError(parseError(e));
                setOtpCode('');
            })
            .finally(() => {
                setIsVerifyingOtp(false);
            });
    }, [email, otpCode]);

    const resendEmailConfirmation = useCallback(() => {
        setIsVerifyingOtp(true);
        setError({});

        axios
            .post('/auth/signup/verify/send', { email })
            .then(() => {
                setTimeLeft(60);
            })
            .catch((e) => {
                setError(parseError(e));
            })
            .finally(() => {
                setIsVerifyingOtp(false);
            });
    }, [email]);

    const finishSignup = useCallback(() => {
        setFinishing(true);
        const signInfo = {
            email,
            password,
            name,
            surname,
            phone: phoneNumber,
            isPhoneVerified,
            address: {
                zipCode
            }
        };

        axios
            .post(`/auth/signup/customer`, signInfo)
            .then(({ data }) => {
                store.auth.authorize(data.token, 'customer');
                navigate('/athena/welcome');
            })
            .catch((e) => {
                setStep(1);
                setError(parseError(e));
            });
    },
        [navigate, store.auth, email, password, name, surname, phoneNumber, isPhoneVerified, zipCode]
    );

    const googleSing = useCallback((data) => {
        setIsGoogleSignUp(true);

        if (data.email) {
            setEmail(data.email);
            setPassword(null);
            if (data.given_name) setName(data.given_name);
            if (data.family_name) setSurname(data.family_name);
        }
    }, []);

    const onNextStep = useCallback(() => {
        setStep((step) => step + 1);

    }, [step]);


    const isZipCodeInRegion = useCallback((zipCode) => {
        if (!zipCode || zipCode.length !== 5) return false;

        const cleanZip = zipCode.trim().substring(0, 5);

        if (!/^\d{5}$/.test(cleanZip)) {
            return false;
        }

        const prefix = parseInt(cleanZip.substring(0, 3), 10);

        const isInMainRange = prefix >= 17 && prefix <= 22;
        const isAdditionalPrefix = prefix === 24;

        return isInMainRange || isAdditionalPrefix;
    }, []);

    const isZipValid = useMemo(() => {
        return isZipCodeInRegion(zipCode);
    }, [zipCode, isZipCodeInRegion]);

    const checkUserExistence = async (e) => {
        e.preventDefault();

        if (!termsAccept) {
            setTermsError(true)
            return
        } else {
            setTermsError(false)
        }

        setIsLoading(true);

        try {
            const { data } = await axios.post('/auth/signup/exist', { email });
            setIsUserExist(data.exists);
            onNextStep();
            if (!data.exists && isGoogleSignUp) {
                finishSignup();
            }
            if (!data.exists) {
                resendEmailConfirmation();
            }
        } catch (error) {
            console.error('Error checking user existence:', error);
        } finally {
            setIsLoading(false)
        }
    };

    const startReset = useCallback(() => {
        setModalState("reset");
        setResetStep(0);
        setResetCode('');
        setPassword('');
    }, []);

    const closeModal = useCallback(() => {
        setModalState("main");
        setTimeLeft(0);
    }, []);

    const resetPassword = useCallback(() => {
        setIsLoading(true);
        setError({});
        if (resetStep === 0) {
            axios.post(`/auth/reset/customer`, { email }).then(({ data }) => {
                store.notif.create({ title: 'Reset started', content: 'Email sent.', type: 'info', autoClose: 15000 });
                setResetStep(1);
            }).catch((e) => setError(parseError(e))).finally(() => setIsLoading(false));
        }
        if (resetStep === 1) { setResetStep(2); setIsLoading(false); }
        if (resetStep === 2) {
            axios.post(`/auth/reset/confirm`, { email, password, code: resetCode.join('') })
                .then(({ data }) => {
                    setResetStep(2);
                    setModalState("main");
                    store.notif.create({ title: 'Success', content: 'Password reset successfully!', type: 'success', autoClose: 10000 });
                })
                .catch((e) => {
                    setError(parseError(e));
                })
                .finally(() => setIsLoading(false));
        }
    }, [store.notif, email, password, resetCode, resetStep]);

    const signInWithPassword = useCallback(() => {
        setIsLoading(true);
        setError({});
        axios
            .post(`/auth/signin/customer`, { email, password })
            .then(({ data }) => {
                store.auth.authorize(data.token, 'customer', data.backgroundCheckResult);
                navigate('/');
            })
            .catch((e) => {
                setError(parseError(e));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [email, password, store.auth, navigate]);

    const googleSignIn = useCallback(
        ({ access_token }) => {
            axios
                .post(`/auth/signin/customer/google`, { access_token })
                .then(({ data }) => {
                    store.auth.authorize(data.token, 'customer', data.backgroundCheckResult);
                    navigate('/');
                })
                .catch((e) => {
                    const message = e.response?.data?.error || 'is invalid';
                    setError({ type: 'Google Auth', message });
                });
        },
        [store.auth, navigate]
    );



    return (
        <div className="relative min-h-screen bg-gray-50 flex flex-col">
            <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
                {step > 0 && !finishing && step < 3 && (
                    <Stepper
                        step={step}
                        nextStep={onNextStep}
                        userType={'customer'}
                        setPhoneNumber={setPhoneNumber}
                        phoneNumber={phoneNumber}
                        setIsPhoneVerified={setIsPhoneVerified}
                        zipCode={zipCode}
                        setZipCode={setZipCode}
                        isZipValid={isZipValid}
                        email={email}
                        setEmail={setEmail}
                    />
                )}

                {step >= 3 && (
                    <>
                        <div className="sm:mx-auto sm:w-full sm:max-w-md">
                            <Link to="/" className="flex justify-center items-center space-x-2 mb-8">
                                <img
                                    src={logo}
                                    alt="Emissary Care"
                                    className="h-12 w-auto"
                                />
                            </Link>
                            {!isUserExist && (
                                <>
                                    <h2 className="mt-6 text-center text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-500 via-sky-500 to-sky-700 bg-clip-text text-transparent">
                                        Create your account
                                    </h2>
                                    <p className="mt-2 text-center text-sm text-gray-600">
                                        Start your journey to better care today
                                    </p>
                                </>
                            )}

                        </div>

                        {step === 4 && !isUserExist && !isGoogleSignUp && (
                            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
                                    <h3 className="text-xl font-bold text-gray-900 mb-4">Check your email</h3>
                                    <p className="text-sm text-gray-600 mb-6">
                                        We've sent instructions to <b>{email}</b>.<br />
                                        Please enter the verification code to continue.
                                    </p>

                                    <form onSubmit={verifyEmailCode} className="space-y-6">

                                        <ConfirmationCode setConfirm={setOtpCode} />

                                        <div className="h-[20px]">
                                            {error.message && (
                                                <div className="text-red-600 text-sm">
                                                    {error.message}
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            type="submit"
                                            className="w-full btn-primary"
                                            disabled={isVerifyingOtp}
                                        >
                                            {isVerifyingOtp ? 'Verifying...' : 'Verify & Continue'}
                                        </button>

                                        <div className="text-sm mt-4">
                                            {timeLeft > 0 ? (
                                                <p className="text-gray-400">Resend code in {timeLeft}s</p>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={resendEmailConfirmation}
                                                    disabled={isVerifyingOtp}
                                                    className="text-blue-600 hover:text-blue-500 font-medium"
                                                >
                                                    Resend Code
                                                </button>
                                            )}
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                        {step === 3 && (
                            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                                    <form className="space-y-6" onSubmit={checkUserExistence}>
                                        <GoogleSign onUserInfo={googleSing} type="up" />
                                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                            <div>
                                                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                                                    First name
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        id="firstName"
                                                        name="firstName"
                                                        type="text"
                                                        required
                                                        value={name}
                                                        onChange={(e) => setName(e.target.value)}
                                                        className="input-primary"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                                                    Last name
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        id="lastName"
                                                        name="lastName"
                                                        type="text"
                                                        required
                                                        value={surname}
                                                        onChange={(e) => setSurname(e.target.value)}
                                                        className="input-primary"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                                Email address
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    autoComplete="email"
                                                    required
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    className="input-primary"
                                                />
                                            </div>
                                        </div>


                                        <div className="h-[20px]">
                                            {error.message && (
                                                <div className="text-red-600 text-sm text-center">
                                                    <b>{error.type}</b> {error.message}
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-6 ">
                                            <label
                                                className={classNames(
                                                    'flex items-start space-x-3 text-sm font-semibold text-gray-700 justify-center',
                                                    { 'text-red-600': termsError }
                                                )}
                                            >
                                                <div className="flex items-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={termsAccept}
                                                        onChange={(e) => {
                                                            setTermsAccept(e.target.checked)
                                                        }}
                                                        className=" h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    />
                                                    <label htmlFor="emailNotifications" className="ml-2 block text-sm text-gray-900">
                                                        <div className="check"></div>I agree to the{' '}
                                                        <Link
                                                            to="/terms"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >Terms</Link> and{' '}
                                                        <Link
                                                            to="/policy"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >Privacy Policy</Link>
                                                    </label>
                                                </div>
                                            </label>
                                        </div>
                                        <div>
                                            <button disabled={!termsAccept} type="submit" className="w-full btn-primary">
                                                Sign up
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}
                        {step === 4 && isUserExist && (
                            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 max-w-md mx-auto">
                                <p className="mt-2 mb-4 text-center text-sm text-gray-600">
                                    It looks like you already have an account with us! Please enter your
                                    password to log in
                                </p>
                                <GoogleSign onSign={googleSignIn} type="in" />

                                <div className="flex items-center w-full mt-6">
                                    <div className="flex-grow border-t border-gray-200"></div>
                                    <span className="px-4 text-sm text-gray-500">or</span>
                                    <div className="flex-grow border-t border-gray-200"></div>
                                </div>

                                <div className="space-y-1.5">
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Password"
                                        className="w-full p-3.5 mt-6 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all "
                                    />
                                </div>

                                <div className="pt-2">
                                    <button onClick={signInWithPassword} className="w-full  mt-4 flex justify-center items-center py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200 bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 text-white shadow-lg shadow-blue-500/20">
                                        {isLoading ? (
                                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        ) : 'Sign In'}
                                    </button>
                                    <button onClick={startReset} className="w-full mt-4 text-sm text-sky-600 hover:text-sky-700 font-medium">
                                        Forgot password?
                                    </button>
                                </div>
                            </div>
                        )}
                        {step === 5 && !isUserExist && !isGoogleSignUp && (
                            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                                    <div className="space-y-6">
                                        <div>
                                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                                Password
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    id="password"
                                                    name="password"
                                                    type="password"
                                                    autoComplete="new-password"
                                                    required
                                                    value={password}
                                                    onChange={(e) => setPassword(e.target.value)}
                                                    className="input-primary"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                                Confirm password
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    id="confirmPassword"
                                                    name="confirmPassword"
                                                    type="password"
                                                    autoComplete="new-password"
                                                    required
                                                    value={passwordRepeat}
                                                    onChange={(e) => setPasswordRepeat(e.target.value)}
                                                    className="input-primary"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <button type="button" onClick={finishSignup} className="w-full btn-primary">
                                                Create Account
                                            </button>

                                            <button onClick={() => setStep(3)} className="w-full mt-4 text-sm text-sky-600 hover:text-sky-700 font-medium">
                                                Back to Sign Up
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {modalState === "reset" && (
                            <BaseModal show={true} onClose={closeModal}>
                                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                                    {/* ... Modal content kept exactly as original ... */}
                                    <div className="sm:flex sm:items-start">
                                        <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-sky-100 sm:mx-0 sm:h-10 sm:w-10">
                                            <svg className="h-6 w-6 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                            </svg>
                                        </div>
                                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900">Reset Password</h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    {resetStep === 0 && "Enter your email to receive a reset code"}
                                                    {resetStep === 1 && "Enter the verification code sent to your email"}
                                                    {resetStep === 2 && "Create your new password"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-6 space-y-4">
                                        {resetStep === 0 && (
                                            <div className="space-y-1.5">
                                                <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700">Email address</label>
                                                <input id="resetEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-primary w-full p-3 border border-gray-300 rounded-lg" placeholder="you@example.com" />
                                            </div>
                                        )}
                                        {resetStep === 1 && (<div className="space-y-4"><ConfirmationCode setConfirm={setResetCode} /></div>)}
                                        {resetStep === 2 && (
                                            <div className="space-y-1.5">
                                                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">New password</label>
                                                <input id="newPassword" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-primary w-full p-3 border border-gray-300 rounded-lg" placeholder="••••••••" />
                                            </div>
                                        )}
                                        <div className="mt-6 space-y-2">
                                            <button onClick={resetPassword} className="w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-sky-600 hover:bg-sky-700">{['Send Reset Code', 'Verify Code', 'Reset Password'][resetStep]}</button>
                                            <button onClick={() => setModalState("main")} className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50">Back to Login</button>
                                        </div>
                                    </div>
                                </div>
                            </BaseModal>
                        )}
                    </>
                )}
                <Loader show={isLoading} />
            </div>
        </div>
    )
}

export default VerificationGates